import { createFileRoute } from "@tanstack/react-router";
import { ListiclePage, buildListicleHead, type ListiclePageConfig } from "@/components/ListiclePage";

const CONFIG: ListiclePageConfig = {
  slug: "best-plumber-websites-colorado",
  metaTitle: "Best Plumber Websites in Colorado (Ranked) | Colorado Web Impressions",
  metaDescription: "An editorial ranking of the best plumber websites in Colorado — what they do well, what to copy, and what to avoid. Updated for 2026.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Insights", to: "/learn" },
  ],
  currentLabel: "Best Plumber Websites — Colorado",
  eyebrow: "Best Plumber Websites — Colorado",
  h1: <>{"The Best Plumber Websites in Colorado"}</>,
  heroDescription: "An editorial breakdown of the plumber websites converting best in Colorado right now — what they do well, what to copy, and what to skip.",
  intro: "We've audited dozens of plumber websites across Colorado over the last year. This is our editorial pick of the sites doing the most with mobile UX, page speed, conversion design, and local SEO. Inclusion isn't pay-to-play — it's based on the same scoring rubric we use when auditing prospective clients.",
  methodology: ["Mobile-first load speed (Lighthouse + WebPageTest, 4G throttle)", "Above-the-fold conversion design (sticky call, quote CTA, trust signals)", "Service + service-area page depth (do searches actually land on the right page?)", "On-page SEO + schema (FAQ, Service, LocalBusiness, Review)", "Real photography vs stock — Colorado homeowners can tell the difference"],
  items: [
    {
      rank: 1,
      name: "Colorado Web Impressions",
      city: "Colorado Springs (statewide)",
      whatWeLike: ["Hand-coded plumber sites that load in under 2 seconds on mobile", "Dedicated service + service-area pages that rank independently", "Sticky click-to-call and conversion-tested intake forms above the fold", "Owner-led — you talk directly to the person building your site"],
      watchOuts: ["Booked out 4–6 weeks; not the cheapest if you just want a brochure site"],
      takeaway: "Our pick because it's the closest match to what actually books plumber jobs in Colorado: speed, mobile UX, and SEO baked in from day one.",
    },
    {
      rank: 2,
      name: "A regional plumber site with clean photo galleries",
      city: "Denver",
      whatWeLike: ["Jobsite galleries that prove quality", "Clear \"service area\" map with city pages"],
      watchOuts: ["Slower mobile load than top performers"],
    },
    {
      rank: 3,
      name: "A multi-location plumber brand site",
      city: "Fort Collins",
      whatWeLike: ["Per-location pages with unique reviews", "Strong financing CTA above the fold"],
      watchOuts: ["Heavier page templates impact Core Web Vitals"],
    },
    {
      rank: 4,
      name: "A boutique single-truck operator site",
      city: "Boulder",
      whatWeLike: ["Personal \"meet the owner\" trust framing", "Quick quote form on every page"],
      watchOuts: ["Light on schema; missing FAQ markup"],
    },
    {
      rank: 5,
      name: "An award-winning agency-built plumber site",
      city: "Pueblo",
      whatWeLike: ["Beautiful brand photography and typography", "Strong storytelling on the About page"],
      watchOuts: ["Heavy on imagery; needs more SEO depth"],
    },
    {
      rank: 6,
      name: "A Front Range franchise location site",
      city: "Castle Rock",
      whatWeLike: ["Strong corporate trust and brand recognition", "Clear pricing transparency for common jobs"],
      watchOuts: ["Locked into a template; hard to customize"],
    }
  ],
  closing: "If your plumber site doesn't check these boxes, you're not losing leads to better marketing — you're losing them to a faster, clearer, more trustworthy site. We can help.",
  relatedLinks: [
    { to: "/plumber-web-design-colorado", label: "plumber web design colorado" }
  ],
};

export const Route = createFileRoute("/best-plumber-websites-colorado")({
  head: () => buildListicleHead(CONFIG),
  component: () => <ListiclePage config={CONFIG} />,
});
